import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ParcelConfigObject } from 'single-spa';

import { useError } from 'hooks/error';
import useRefState from 'hooks/useRefState';
import System from 'system';
import { MainErrorItem, ModuleMeta, PluginModule } from 'types';
import { convertExceptionToJson, findModule, getModulesConfig, logger } from 'utils';
import ErrorView from 'view/components/ErrorView/ErrorView';
import FullScreenLoader from 'view/components/FullScreenLoader/FullScreenLoader';

export type ModuleState = {
  loading: boolean;
  module: PluginModule | ParcelConfigObject | null;
  meta: ModuleMeta | null;
  error: MainErrorItem | null;
};

export type ModulesMap = Record<string, ModuleState>;

export type GetModulesMap = () => ModulesMap;

export type ModuleLoaderValue = {
  modulesMap: ModulesMap;
  load: (moduleKey: string, options?: { force?: boolean }) => void;
  unload: (moduleKey: string) => void;
  reload: (moduleKey: string) => void;
  getModulesMap: GetModulesMap;
};

export const ModuleLoaderContext = createContext<ModuleLoaderValue | null>(null);

const ModuleLoaderProvider = ({ children }) => {
  const { ready } = useTranslation();

  const modulesConfig = getModulesConfig();
  const modulesConfigRef = useRef(modulesConfig);
  modulesConfigRef.current = modulesConfig;

  const [, setModulesMap, getModulesMap] = useRefState<ModulesMap>({});

  const load = useCallback<ModuleLoaderValue['load']>(
    async (moduleKey, { force = false } = {}) => {
      const modulesMap = getModulesMap();
      const moduleState = modulesMap[moduleKey] || { loading: false, error: null, module: null };
      if (moduleState.loading || ((moduleState.module || moduleState.error) && !force)) {
        return;
      }

      const moduleDescriptor = findModule(modulesConfigRef.current || [], moduleKey).module;
      const moduleId = moduleDescriptor?.id;
      if (!moduleId) {
        const errorMessage = `Error loading module: module descriptor for '${moduleKey}' not found!`;
        logger.error(errorMessage);

        const loadStatus = {
          loading: false,
          error: {
            title: 'errors.module_load_error.title',
            message: 'errors.common_message',
            details: {
              type: 'MODULE_DESCRIPTOR_NOT_FOUND',
              timestamp: new Date(),
              error: errorMessage,
            },
          },
          module: null,
          meta: null,
        };
        setModulesMap({
          ...modulesMap,
          [moduleKey]: loadStatus,
        });
        return;
      }

      const meta = {
        moduleId,
        url: System.resolve(moduleId),
        moduleConfig: moduleDescriptor.config,
      };

      setModulesMap({
        ...modulesMap,
        [moduleKey]: { loading: true, module: null, error: null, meta },
      });
      try {
        const module = await System.import(moduleId);
        setModulesMap({
          ...getModulesMap(),
          [moduleKey]: {
            loading: false,
            error: null,
            module: module as ParcelConfigObject | PluginModule,
            meta,
          },
        });
      } catch (error: unknown) {
        logger.error(`Error loading module: '${moduleId}'!`, error, meta);
        setModulesMap({
          ...getModulesMap(),
          [moduleKey]: {
            loading: false,
            error: {
              title: 'errors.module_load_error.title',
              message: 'errors.common_message',
              details: {
                type: 'MODULE_LOAD_ERROR',
                timestamp: new Date(),
                meta: JSON.parse(JSON.stringify(meta)),
                error: convertExceptionToJson(error),
              },
            },
            module: null,
            meta,
          },
        });
        throw error;
      }
    },
    [getModulesMap, setModulesMap],
  );

  const unload = useCallback<ModuleLoaderValue['unload']>(
    (moduleKey) => {
      const moduleId = findModule(modulesConfigRef.current || [], moduleKey).module?.id;
      if (!moduleId) {
        return;
      }
      System.delete(System.resolve(moduleId));
      const modulesMap = { ...getModulesMap() };
      delete modulesMap[moduleKey];
      setModulesMap(modulesMap);
    },
    [getModulesMap, setModulesMap],
  );

  const reload = useCallback<ModuleLoaderValue['reload']>(
    (moduleKey) => {
      unload(moduleKey);
      load(moduleKey);
    },
    [load, unload],
  );
  const modulesMap = getModulesMap();
  const contextValue = useMemo(
    () => ({ modulesMap, load, unload, reload, getModulesMap, modulesFromConfig: modulesConfig }),
    [modulesMap, getModulesMap, load, reload, unload, modulesConfig],
  );

  // display error when configs are not loaded
  const [configsAreLoaded, setConfigsLoaded] = useState(false);
  const { main: error } = useError();
  useEffect(() => {
    if (error) {
      return;
    }
    setConfigsLoaded(true);
  }, [error]);

  if (!configsAreLoaded || !ready) {
    return error && !configsAreLoaded ? <ErrorView {...error} /> : <FullScreenLoader />;
  }

  return <ModuleLoaderContext.Provider value={contextValue}>{children}</ModuleLoaderContext.Provider>;
};

ModuleLoaderProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.string]).isRequired,
};

export default ModuleLoaderProvider;
